import { useMemo, useRef, useState } from "react";
import { ColDef, ColumnState, RowClickedEvent } from "ag-grid-community";
import { AgGridReact } from "ag-grid-react";
import "ag-grid-community/styles/ag-grid.css";
import "ag-grid-community/styles/ag-theme-alpine.css";
import { GridContextWrapper } from "src/components/common/gridWrapper/GridContextWrapper";
import Grid_Select from "src/components/common/gridWrapper/Grid_Select";
import {
  aggridHeaderClass,
  pdaCellStyle,
} from "src/components/common/common";
import { PDA1050_R01HeaderDto } from "./PDA1050_R01Dto";

interface PDA1050_R01OrderListProps {
  orders: PDA1050_R01HeaderDto[];
  onSelectOrder: (order: PDA1050_R01HeaderDto) => void;
}

type PDA1050_R01HeaderDtoWithInserted = PDA1050_R01HeaderDto & {
  inserted?: boolean;
};

/**
 * 검색된 발주 Header 목록을 그리드로 표시하고 선택한 행을 상세 조회 흐름에 전달한다.
 * 그리드 컬럼 상태는 화면 전용 localStorage key로 보존한다.
 */
const PDA1050_R01OrderList = ({
  orders,
  onSelectOrder,
}: PDA1050_R01OrderListProps) => {
  const gridRef = useRef<AgGridReact<PDA1050_R01HeaderDtoWithInserted>>(null);
  const gridWrapperRef = useRef<HTMLDivElement>(null);
  const [hoveredRowIndex, setHoveredRowIndex] = useState<number | null>(null);
  const [columnState, setColumnState] = useState<ColumnState[] | null>(null);

  /** 부모에서 받은 목록의 참조 변경에 맞춰 그리드용 배열을 다시 생성한다. */
  const paddedGridData = useMemo(() => [...orders], [orders]);

  /** 발주 목록에 표시할 일자, 거래처 및 발주번호 컬럼 정의를 최초 1회 생성한다. */
  const columnDefs = useMemo(
    () =>
      [
        {
          headerName: "발주일자",
          field: "orderDt",
          valueGetter: (params) => params.data?.orderDt || "-",
          cellStyle: {
            textAlign: "center",
            ...pdaCellStyle,
            justifyContent: "center",
          },
          headerClass: aggridHeaderClass,
          flex: 1,
          minWidth: 100,
        },
        {
          headerName: "상호",
          field: "customNm",
          valueGetter: (params) => params.data?.customNm || "-",
          cellStyle: {
            textAlign: "left",
            ...pdaCellStyle,
          },
          headerClass: aggridHeaderClass,
          flex: 1.5,
          minWidth: 120,
        },
        {
          headerName: "발주번호",
          field: "purchaseOrderNo",
          valueGetter: (params) => params.data?.purchaseOrderNo || "-",
          cellStyle: {
            textAlign: "left",
            ...pdaCellStyle,
          },
          headerClass: aggridHeaderClass,
          flex: 2,
          minWidth: 100,
        },
      ] as ColDef<PDA1050_R01HeaderDtoWithInserted>[],
    []
  );

  /** GridContextWrapper가 요구하는 선택 초기화 콜백이며, 이 목록은 별도 선택 상태를 보관하지 않는다. */
  const initSelected = () => {};

  /**
   * 실제 데이터가 있는 행을 클릭했을 때만 부모의 발주 선택 처리를 호출한다.
   *
   * @param event ag-Grid 행 클릭 이벤트
   */
  const handleRowClick = (
    event: RowClickedEvent<PDA1050_R01HeaderDtoWithInserted>
  ) => {
    if (event.data) {
      onSelectOrder(event.data);
    }
  };

  return (
    <div className="flex-1 p-3 pt-2 flex w-full h-full flex-col overflow-hidden">
      <GridContextWrapper<PDA1050_R01HeaderDtoWithInserted>
        gridRef={gridRef}
        gridWrapperRef={gridWrapperRef}
        paddedGridData={paddedGridData}
        initSelected={initSelected}
        baseColumnDefs={columnDefs}
        setColumnState={setColumnState}
        localStorageKey="PDA1050_R01_OrderList_ColumnState"
        enableDataHistory={false}
      >
        <div className="flex-1 w-full h-full min-h-0">
          <Grid_Select<PDA1050_R01HeaderDtoWithInserted>
            ref={gridRef}
            rowData={paddedGridData}
            columnDefs={columnDefs}
            setHoveredRowIndex={setHoveredRowIndex}
            columnStateKey="PDA1050_R01_OrderList_ColumnState"
            setColumnState={setColumnState}
            onRowClicked={handleRowClick}
          />
        </div>
      </GridContextWrapper>
    </div>
  );
};

export default PDA1050_R01OrderList;
